import "@wmf/groove/app.css";
import "./grooveAdapter.css";
import { GroovePlayer, PlaybackNotation, grooveToAbc, parseUrl, totalCells } from "@wmf/groove";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { PlaybackNumberInput } from "./ui/PlaybackNumberInput.js";
import { GroovePracticeLog } from "./GroovePracticeLog.js";
import { GrooveLearnMode, type GrooveLearnTempoSave } from "./GrooveLearnMode.js";

export interface GroovePracticeInstruction {
  id: string;
  position: number;
  note: string;
  targetTempo: number | null;
}

interface SpeedUpSettings {
  enabled: boolean;
  bpm: number;
  everyBars: number;
  targetTempo: number;
}

function defaultSpeedUp(tempo: number): SpeedUpSettings {
  return { enabled: false, bpm: 2, everyBars: 4, targetTempo: Math.min(300, tempo + 20) };
}

function playbackOptions(settings: SpeedUpSettings) {
  return {
    rampBpm: settings.enabled ? settings.bpm : 0,
    rampEveryBars: settings.everyBars,
    targetTempo: settings.targetTempo,
  };
}

/**
 * Read-only, playable Groove snapshot.
 *
 * Public lesson-note links and authenticated repertoire material both use this
 * same player. The caller supplies frozen teaching content; this component
 * never reads the editable Groove library.
 */
export function GrooveSnapshotPlayer({
  title,
  query,
  practiceInstructions = [],
  embedded = false,
  compact = false,
  idPrefix = "public-groove",
  practiceTarget,
  tempoSave,
}: {
  title: string;
  query: string;
  practiceInstructions?: GroovePracticeInstruction[];
  embedded?: boolean;
  compact?: boolean;
  /** Inline task players can coexist on the same page. */
  idPrefix?: string;
  practiceTarget?: string;
  /** Only an authorized tutor lesson view supplies a persistence callback. */
  tempoSave?: GrooveLearnTempoSave;
}) {
  const instanceId = useId();
  const prefix = embedded ? `lesson-groove-${instanceId}` : idPrefix;
  const groove = useMemo(() => parseUrl(query), [query]);
  const [playing, setPlaying] = useState(false);
  const [currentCell, setCurrentCell] = useState<number | null>(null);
  const [tempo, setTempo] = useState<number>(groove.tempo);
  const [tempoInput, setTempoInput] = useState(String(groove.tempo));
  const [speedUp, setSpeedUp] = useState(() => defaultSpeedUp(groove.tempo));
  const [liveBpm, setLiveBpm] = useState<number | null>(null);
  const [activeInstructionId, setActiveInstructionId] = useState<string | null>(null);
  const playerRef = useRef<GroovePlayer | null>(null);

  useEffect(() => {
    playerRef.current?.stop();
    setPlaying(false);
    setCurrentCell(null);
    setTempo(groove.tempo);
    setTempoInput(String(groove.tempo));
    setSpeedUp(defaultSpeedUp(groove.tempo));
    setLiveBpm(null);
    setActiveInstructionId(null);
  }, [groove]);

  useEffect(() => () => playerRef.current?.dispose(), []);

  const playbackGroove = useMemo(() => ({ ...groove, tempo }), [groove, tempo]);

  const getPlayer = useCallback((): GroovePlayer => {
    if (!playerRef.current) {
      playerRef.current = new GroovePlayer();
      playerRef.current.onTempoChange = (bpm) => setLiveBpm(bpm);
    }
    playerRef.current.onStep = compact ? null : setCurrentCell;
    return playerRef.current;
  }, [compact]);

  useEffect(() => {
    if (playerRef.current) playerRef.current.onStep = compact ? null : setCurrentCell;
    setCurrentCell(null);
  }, [compact]);

  useEffect(() => {
    setLiveBpm(null);
    playerRef.current?.updateGroove(playbackGroove);
  }, [playbackGroove]);

  const handlePlay = useCallback(() => {
    const player = getPlayer();
    if (playing) {
      player.stop();
      setPlaying(false);
      setCurrentCell(null);
      setLiveBpm(null);
    } else {
      setCurrentCell(null);
      player.start(playbackGroove, playbackOptions(speedUp));
      setPlaying(true);
    }
  }, [playbackGroove, playing, speedUp, getPlayer]);

  const updateSpeedUp = useCallback(
    (change: Partial<SpeedUpSettings>, startingTempo = tempo) => {
      const next = { ...speedUp, ...change };
      setSpeedUp(next);
      setLiveBpm(null);
      setCurrentCell(null);
      if (playing) getPlayer().start({ ...groove, tempo: startingTempo }, playbackOptions(next));
    },
    [playing, groove, tempo, speedUp, getPlayer],
  );

  const abc = useMemo(() => grooveToAbc(playbackGroove), [playbackGroove]);

  function commitTempoInput() {
    const typed = tempoInput.trim();
    const parsed = Number(typed);
    const next =
      typed !== "" && Number.isFinite(parsed)
        ? Math.max(20, Math.min(300, Math.round(parsed)))
        : tempo;
    applyTempo(next);
  }

  function applyTempo(next: number) {
    setTempo(next);
    setTempoInput(String(next));
    if (speedUp.targetTempo < next) {
      if (speedUp.enabled) updateSpeedUp({ targetTempo: next }, next);
      else setSpeedUp((settings) => ({ ...settings, targetTempo: next }));
    }
  }

  return (
    <section className="public-groove">
      {!embedded && <h1>{title}</h1>}

      <div className="public-groove-controls">
        <div className="public-groove-tempo">
          <label htmlFor={`${prefix}-tempo`}><strong>Tempo</strong></label>
          <input
            id={`${prefix}-tempo`}
            type="number"
            min={20}
            max={300}
            inputMode="numeric"
            value={tempoInput}
            onChange={(event) => setTempoInput(event.target.value)}
            onBlur={commitTempoInput}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
            }}
          />
          <span>BPM</span>
          {activeInstructionId && (
            <span className="small muted">Target selected — you can still slow it down.</span>
          )}
        </div>

        <div className="public-groove-tempo">
          <label htmlFor={`${prefix}-ramp`}><strong>Speed up</strong></label>
          <select
            id={`${prefix}-ramp`}
            value={speedUp.enabled ? "on" : "off"}
            onChange={(event) => updateSpeedUp({ enabled: event.target.value === "on" })}
          >
            <option value="off">Off</option>
            <option value="on">On</option>
          </select>
        </div>
      </div>

      {speedUp.enabled && (
        <fieldset className="public-groove-ramp-controls">
          <legend>Speed-up settings</legend>
          <div className="public-groove-ramp-grid">
            <div className="public-groove-ramp-setting">
              <label htmlFor={`${prefix}-increase`}><strong>Increase by</strong></label>
              <div><PlaybackNumberInput id={`${prefix}-increase`} value={speedUp.bpm} min={1} max={100}
                onCommit={(bpm) => updateSpeedUp({ bpm })} /><span>BPM</span></div>
            </div>
            <div className="public-groove-ramp-setting">
              <label htmlFor={`${prefix}-bars`}><strong>Every</strong></label>
              <div><PlaybackNumberInput id={`${prefix}-bars`} value={speedUp.everyBars} min={1} max={1000}
                onCommit={(everyBars) => updateSpeedUp({ everyBars })} /><span>bars</span></div>
            </div>
            <div className="public-groove-ramp-setting">
              <label htmlFor={`${prefix}-target`}><strong>Target speed</strong></label>
              <div><PlaybackNumberInput id={`${prefix}-target`} value={speedUp.targetTempo} min={tempo} max={300}
                onCommit={(targetTempo) => updateSpeedUp({ targetTempo })} /><span>BPM</span></div>
            </div>
          </div>
          <p className="small muted public-groove-ramp-summary">
            Increase by {speedUp.bpm} BPM every {speedUp.everyBars} {speedUp.everyBars === 1 ? "bar" : "bars"},
            then keep playing at {speedUp.targetTempo} BPM.
            {playing && " Changing these settings restarts from the tempo above."}
          </p>
          {playing && <p className="public-groove-live-bpm" role="status">
            now {liveBpm ?? tempo} BPM
            {(liveBpm ?? tempo) >= speedUp.targetTempo && " — Target reached"}
          </p>}
        </fieldset>
      )}

      <button
        type="button"
        className="primary groove-transport-button"
        onClick={handlePlay}
        aria-pressed={playing}
        data-groove-transport
      >
        {playing ? "Stop" : "Play"}
      </button>
      <span className="groove-space-hint"> Spacebar to play/stop on a computer</span>
      <GroovePracticeLog playing={playing} title={title} practiceTarget={practiceTarget}/>
      <GrooveLearnMode query={query} title={title} {...tempoSave} onOpen={() => {
        playerRef.current?.stop();
        setPlaying(false);
        setCurrentCell(null);
        setLiveBpm(null);
      }} />

      {!compact && (
        <div className="groove-scope">
          <PlaybackNotation abc={abc} single={groove.measures === 1}
            currentCell={currentCell} totalCells={totalCells(groove)} />
        </div>
      )}

      {practiceInstructions.length > 0 && (
        <section className="public-groove-practice" aria-labelledby={`${prefix}-practice-title`}>
          <h2 id={`${prefix}-practice-title`}>Your practice</h2>
          <p className="small muted">
            Choose a step to load your tutor's tempo. You can slow it down and set your own
            speed-up target without changing their practice instructions.
          </p>
          <div className="public-groove-practice-list">
            {practiceInstructions.map((instruction) => {
              const selected = activeInstructionId === instruction.id;
              const target = instruction.targetTempo ?? groove.tempo;
              return (
                <button
                  key={instruction.id}
                  type="button"
                  className="public-groove-practice-step"
                  aria-pressed={selected}
                  onClick={() => {
                    setActiveInstructionId(instruction.id);
                    applyTempo(target);
                  }}
                >
                  <span>{instruction.note.trim() || `Practice step ${instruction.position + 1}`}</span>
                  <strong>
                    {instruction.targetTempo === null
                      ? "Saved tempo"
                      : `${instruction.targetTempo} BPM`}
                  </strong>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </section>
  );
}
